import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMyScheduledChargeDebt,
  getMyScheduledCharges,
  ScheduledChargeInstallmentView,
} from 'services/api.service';
import { maskCurrency } from 'utils/number';

const STATUS_LABEL: Record<string, string> = {
  AGENDADA: 'Agendada',
  LIQUIDADA: 'Paga',
  PARCIALMENTE_LIQUIDADA: 'Parcialmente paga',
  EM_ABERTO: 'Em aberto',
  CANCELADA: 'Cancelada',
  BAIXADA_MANUALMENTE: 'Baixada',
};

const STATUS_COLOR: Record<string, string> = {
  AGENDADA: 'blue',
  LIQUIDADA: 'green',
  PARCIALMENTE_LIQUIDADA: 'yellow',
  EM_ABERTO: 'orange',
  CANCELADA: 'gray',
  BAIXADA_MANUALMENTE: 'purple',
};

const dateBR = (iso?: string | null) =>
  iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—';

// Rótulo e valor em aberto vêm prontos do servidor: um registro de tipos só, e
// a conta em Decimal em vez de float.
const outstandingOf = (row: ScheduledChargeInstallmentView): number => Number(row.outstanding);
const labelOf = (row: ScheduledChargeInstallmentView): string =>
  row.typeLabel || 'Cobrança programada';

/**
 * Visão do pagador: o que ele deve, o que vem pela frente e o histórico.
 *
 * O botão "Pagar fatura" leva para a tela de depósito com o valor da dívida já
 * preenchido — o depósito credita a conta e, com a conta negativa, o crédito
 * abate a dívida direto.
 */
export default function ScheduledChargesPage() {
  const navigate = useNavigate();
  const [onlyOpen, setOnlyOpen] = React.useState(true);

  const { data: debt, isLoading: loadingDebt } = useQuery(['scheduled-charges-debt'], () =>
    getMyScheduledChargeDebt(),
  );

  const { data, isLoading } = useQuery(['scheduled-charges-me', onlyOpen], () =>
    getMyScheduledCharges({ limit: 200, ...(onlyOpen ? { onlyOverdue: true } : {}) }),
  );

  // `data?.data ?? []` cria um array novo a cada render; memoizar aqui é o que
  // impede o useMemo de baixo de recalcular sempre.
  const rows = React.useMemo(() => data?.data ?? [], [data]);
  const upcoming = React.useMemo(
    () =>
      rows
        .filter((row) => row.status === 'AGENDADA')
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
        .slice(0, 5),
    [rows],
  );

  const totalDebt = Number(debt?.total ?? 0);

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: 4, md: 0 }}>
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
        <Heading size="lg">Cobranças programadas</Heading>
        {totalDebt > 0 && (
          <Button
            colorScheme="green"
            onClick={() => navigate(`/access/deposit?amount=${totalDebt.toFixed(2)}`)}
          >
            Pagar fatura ({maskCurrency(totalDebt)})
          </Button>
        )}
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} mb={6}>
        <Box bg="white" borderRadius="20px" p={5} boxShadow="sm">
          <Stat>
            <StatLabel fontSize="sm">Saldo devedor</StatLabel>
            <StatNumber fontSize="xl" color={totalDebt > 0 ? 'red.500' : 'green.500'}>
              {loadingDebt ? <Spinner size="sm" /> : maskCurrency(totalDebt)}
            </StatNumber>
          </Stat>
        </Box>
        <Box bg="white" borderRadius="20px" p={5} boxShadow="sm">
          <Stat>
            <StatLabel fontSize="sm">Cobranças em aberto</StatLabel>
            <StatNumber fontSize="xl">{debt?.installments ?? 0}</StatNumber>
          </Stat>
        </Box>
        <Box bg="white" borderRadius="20px" p={5} boxShadow="sm">
          <Stat>
            <StatLabel fontSize="sm">Vencimento mais antigo</StatLabel>
            <StatNumber fontSize="xl">{dateBR(debt?.oldestDueDate)}</StatNumber>
          </Stat>
        </Box>
      </SimpleGrid>

      {totalDebt > 0 && (
        <Alert status="warning" borderRadius="12px" mb={6}>
          <AlertIcon />
          <Text fontSize="sm">
            {Number(debt?.negativeBalance ?? 0) > 0
              ? `Sua conta está negativa em ${maskCurrency(
                  Number(debt?.negativeBalance),
                )} por cobranças já debitadas — enquanto isso, incide o juros do limite de conta. `
              : ''}
            {Number(debt?.openInstallments ?? 0) > 0
              ? `Há ${maskCurrency(
                  Number(debt?.openInstallments),
                )} em cobranças vencidas aguardando saldo. `
              : ''}
            Deposite para regularizar — qualquer entrada abate a dívida automaticamente.
          </Text>
        </Alert>
      )}

      {upcoming.length > 0 && (
        <Box bg="white" borderRadius="20px" p={6} boxShadow="sm" mb={6}>
          <Heading size="sm" mb={4}>
            Próximas cobranças
          </Heading>
          <VStack align="stretch" spacing={2}>
            {upcoming.map((row) => (
              <Flex key={row.id} justify="space-between" fontSize="sm">
                <Text>
                  {labelOf(row)} — parcela {row.number}
                </Text>
                <HStack>
                  <Text color="gray.500">{dateBR(row.dueDate)}</Text>
                  <Text fontWeight="semibold">{maskCurrency(Number(row.originalAmount))}</Text>
                </HStack>
              </Flex>
            ))}
          </VStack>
        </Box>
      )}

      <Box bg="white" borderRadius="20px" p={6} boxShadow="sm">
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="sm">Histórico</Heading>
          <Button size="sm" variant="outline" onClick={() => setOnlyOpen(!onlyOpen)}>
            {onlyOpen ? 'Ver todas' : 'Ver só em atraso'}
          </Button>
        </Flex>

        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner />
          </Flex>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Cobrança</Th>
                <Th>Recebedor</Th>
                <Th>#</Th>
                <Th>Vencimento</Th>
                <Th isNumeric>Valor</Th>
                <Th isNumeric>Em aberto</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td>{labelOf(row)}</Td>
                  <Td>{row.scheduledCharge?.Receiver?.name ?? '—'}</Td>
                  <Td>{row.number}</Td>
                  <Td>
                    {dateBR(row.dueDate)}
                    {Number(row.daysOverdue ?? 0) > 0 && (
                      <Badge ml={2} colorScheme="red" fontSize="0.6rem">
                        {row.daysOverdue}d
                      </Badge>
                    )}
                  </Td>
                  <Td isNumeric>{maskCurrency(Number(row.originalAmount))}</Td>
                  <Td isNumeric>{maskCurrency(outstandingOf(row))}</Td>
                  <Td>
                    <Badge colorScheme={STATUS_COLOR[row.status] ?? 'gray'}>
                      {STATUS_LABEL[row.status] ?? row.status}
                    </Badge>
                  </Td>
                </Tr>
              ))}
              {rows.length === 0 && (
                <Tr>
                  <Td colSpan={7}>
                    <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                      Nenhuma cobrança programada.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>
    </Box>
  );
}
